/**
 * Notyf Helpers - Standardized notification system
 * Uses the modern notification system from notification.js
 */

import {
  showNotification,
  showLoadingNotification,
  hideNotification,
  clearAllNotifications,
} from "./notification.js";

export const USER_MESSAGES = {
  LOADING: "Loading...",
  ERROR: "An error occurred",
  SUCCESS: "Success",
  NETWORK_ERROR: "Network error occurred",
  INVALID_DATA: "Invalid data received",
};

/**
 * Show user-friendly notification
 * @param {string} message - Message to display
 * @param {string} type - Type of notification (success, error, warning, info)
 * @param {number} duration - Duration in milliseconds
 * @returns {HTMLElement} Notification element
 */
export function showUserFriendlyNotyf(message, type = "success", duration = 3000) {
  const isError = type === "error";
  return showNotification(message, isError, duration);
}

/**
 * Simple translation function
 * @param {string} text - Text to translate
 * @returns {string} Translated text (currently returns as-is)
 */
export function __(text) {
  return text; // Simple translation stub
}

/**
 * Show error notification
 * @param {string} message - Error message
 * @param {number} duration - Duration in milliseconds
 * @returns {HTMLElement} Notification element
 */
export function showErrorNotyf(message, duration = 5000) {
  return showNotification(message, true, duration);
}

/**
 * Show success notification
 * @param {string} message - Success message
 * @param {number} duration - Duration in milliseconds
 * @returns {HTMLElement} Notification element
 */
export function showSuccessNotyf(message, duration = 3000) {
  return showNotification(message, false, duration);
}

/**
 * Show warning notification
 * @param {string} message - Warning message
 * @param {number} duration - Duration in milliseconds
 * @returns {HTMLElement} Notification element
 */
export function showWarningNotyf(message, duration = 4000) {
  return showNotification(message, false, duration);
}

/**
 * Show info notification
 * @param {string} message - Info message
 * @param {number} duration - Duration in milliseconds
 * @returns {HTMLElement} Notification element
 */
export function showInfoNotyf(message, duration = 3000) {
  return showNotification(message, false, duration);
}

/**
 * Show loading notification
 * @param {string} message - Loading message
 * @param {Object} options - Loading options
 * @returns {HTMLElement} Notification element
 */
export function showLoadingNotyf(message, options = {}) {
  return showLoadingNotification(message, options);
}

/**
 * Hide specific notification
 * @param {HTMLElement} notification - Notification element to hide
 */
export function hideNotyf(notification) {
  hideNotification(notification);
}

/**
 * Clear all notifications
 */
export function clearAllNotyf() {
  clearAllNotifications();
}
